<ng-container *ngIf="state.state$ | async as draft">
    <section class="step step-review">
        <header class="step-header">
            <div class="step-header__icon" aria-hidden="true">
                <mat-icon>fact_check</mat-icon>
            </div>
            <div class="step-header__text">
                <h2>Almost there — give it a final look</h2>
                <p class="lede">Everything below will be created for <strong>{{ draft.basics.name || 'your organization' }}</strong>. Jump back to any step to edit before you hit create.</p>
            </div>
        </header>

        <mat-accordion multi>
            <mat-expansion-panel expanded>
                <mat-expansion-panel-header>
                    <mat-panel-title>Source</mat-panel-title>
                    <mat-panel-description>
                        {{ draft.sourceMode | titlecase }}
                        <ng-container *ngIf="draft.sourceRef?.scaffoldVariant">
                            · {{ draft.sourceRef!.scaffoldVariant }}
                        </ng-container>
                    </mat-panel-description>
                </mat-expansion-panel-header>
                <p class="panel-body">
                    <ng-container *ngIf="draft.sourceMode === 'blank'">
                        Your organization will be created with no preset structure. You'll build it out from the org board.
                    </ng-container>
                    <ng-container *ngIf="draft.sourceMode === 'scaffold'">
                        We'll seed a CEO post plus a starting Division and Department.
                        <span *ngIf="draft.sourceRef?.scaffoldVariant === 'with-executive-team'">
                            An Executive Assistant post will also be created in the CEO's header strip.
                        </span>
                    </ng-container>
                    <ng-container *ngIf="draft.sourceMode === 'excel'">
                        Structure was imported from an Excel file. The {{ draft.structure.length }} part(s) below will
                        be created as-is — edit them on the Structure step if anything needs to change.
                    </ng-container>
                </p>
            </mat-expansion-panel>

            <mat-expansion-panel expanded>
                <mat-expansion-panel-header>
                    <mat-panel-title>Basics</mat-panel-title>
                    <mat-panel-description>{{ draft.basics.name || '(no name yet)' }}</mat-panel-description>
                </mat-expansion-panel-header>
                <div class="kv" *ngIf="draft.basics.description; else basicsEmpty">
                    <div>
                        <span class="k">Description</span>
                        <span class="v">{{ draft.basics.description }}</span>
                    </div>
                </div>
                <ng-template #basicsEmpty>
                    <p class="panel-body muted">No description set. You can add one later from the org settings.</p>
                </ng-template>
            </mat-expansion-panel>

            <mat-expansion-panel>
                <mat-expansion-panel-header>
                    <mat-panel-title>Structure</mat-panel-title>
                    <mat-panel-description>{{ draft.structure.length }} part(s)</mat-panel-description>
                </mat-expansion-panel-header>
                <p class="ceo-summary">
                    <span class="ceo-summary-glyph" aria-hidden="true">👑</span>
                    <span>
                        CEO hat will be assigned to:
                        <strong>{{ ceoPostLabel(draft.structure) }}</strong>
                    </span>
                </p>
                <ng-container *ngIf="draft.structure.length; else emptyStructure">
                    <ul class="tree-preview">
                        <li *ngFor="let p of draft.structure" [style.padding-left.px]="p.depth * 16">
                            <mat-icon class="part-icon">{{ p.partType === 'post' ? 'person' : 'folder' }}</mat-icon>
                            <strong>{{ p.name }}</strong>
                            <span class="muted">— {{ p.partType }}</span>
                        </li>
                    </ul>
                </ng-container>
                <ng-template #emptyStructure>
                    <p class="muted">No parts yet. You'll build out the structure on the org board after creation.</p>
                </ng-template>
            </mat-expansion-panel>

            <mat-expansion-panel expanded>
                <mat-expansion-panel-header>
                    <mat-panel-title>Staff</mat-panel-title>
                    <mat-panel-description>
                        {{ draft.staff.length }} invite{{ draft.staff.length === 1 ? '' : 's' }}
                        · you become CEO
                    </mat-panel-description>
                </mat-expansion-panel-header>

                <p class="muted approval-callout">
                    <mat-icon>shield</mat-icon>
                    <span>
                        Approval workflow is <strong>ON</strong> for new organizations. Everyone you add
                        below will land as <strong>pending</strong> until an admin approves them on the
                        org board. You (the CEO) are auto-approved so the org is usable immediately.
                    </span>
                </p>

                <ng-container *ngIf="draft.staff.length; else emptyStaff">
                    <ul class="staff-preview">
                        <li *ngFor="let row of draft.staff" class="staff-preview__row">
                            <mat-icon
                                class="staff-preview__icon"
                                [class.is-existing]="row.resolutionStatus === 'existing-user'"
                                [class.is-invitable]="row.resolutionStatus === 'invitable'"
                                [class.is-invalid]="row.resolutionStatus === 'invalid'"
                                [matTooltip]="statusLabel(row.resolutionStatus)"
                            >{{ statusIcon(row.resolutionStatus) }}</mat-icon>
                            <span class="staff-preview__email">{{ row.email || '(no email)' }}</span>
                            <span class="staff-preview__name muted" *ngIf="row.firstName || row.lastName">
                                {{ row.firstName }} {{ row.lastName }}
                            </span>
                            <span class="staff-preview__post-chip" [matTooltip]="'Assigned to ' + postLabelFor(draft.structure, row.postKey)">
                                <mat-icon>badge</mat-icon>
                                {{ postLabelFor(draft.structure, row.postKey) }}
                            </span>
                            <span class="staff-preview__role-chip" [attr.data-role]="(row.role || 'member')">
                                {{ row.role || 'member' }}
                            </span>
                        </li>
                    </ul>
                </ng-container>
                <ng-template #emptyStaff>
                    <p class="muted">
                        No additional staff yet. You'll be added as the CEO/owner automatically; invite
                        the rest of your team from the org board after creation.
                    </p>
                </ng-template>
            </mat-expansion-panel>

            <mat-expansion-panel>
                <mat-expansion-panel-header>
                    <mat-panel-title>Plan &amp; Features</mat-panel-title>
                    <mat-panel-description>{{ featureSummary }}</mat-panel-description>
                </mat-expansion-panel-header>

                <ng-container *ngIf="!featuresLoading; else loadingFeatures">
                    <p *ngIf="featuresLoadError" class="muted">
                        {{ featuresLoadError }} — features will still be created on submit.
                    </p>

                    <p *ngIf="!hasFeatureSelection && !featuresLoadError" class="muted">
                        You haven't customised features yet. Every feature ({{ totalCount }}) will be enabled
                        on the new organization. Open Step 5 to narrow this down.
                    </p>

                    <ng-container *ngIf="hasFeatureSelection && bundleSummaries.length">
                        <ul class="bundle-summary">
                            <li *ngFor="let b of bundleSummaries"
                                [class.locked]="b.locked"
                                [class.partial]="b.selectedCount > 0 && b.selectedCount < b.totalCount"
                                [class.empty]="b.selectedCount === 0">
                                <mat-icon *ngIf="b.locked" matTooltip="Always on — required core features">lock</mat-icon>
                                <mat-icon *ngIf="!b.locked && b.selectedCount === b.totalCount">check_circle</mat-icon>
                                <mat-icon *ngIf="!b.locked && b.selectedCount > 0 && b.selectedCount < b.totalCount">remove_circle</mat-icon>
                                <mat-icon *ngIf="!b.locked && b.selectedCount === 0">radio_button_unchecked</mat-icon>
                                <span class="bundle-name">{{ b.name }}</span>
                                <span class="bundle-count muted">{{ b.selectedCount }} / {{ b.totalCount }}</span>
                            </li>
                        </ul>
                        <p class="muted footnote" *ngIf="systemFeatureCount > 0">
                            + {{ systemFeatureCount }} system feature{{ systemFeatureCount === 1 ? '' : 's' }} (always on, included automatically).
                        </p>
                    </ng-container>
                </ng-container>

                <ng-template #loadingFeatures>
                    <p class="muted">Loading feature catalogue…</p>
                </ng-template>
            </mat-expansion-panel>
        </mat-accordion>

        <!-- R9.3 — Live progress timeline shown once submit fires. -->
        <section class="progress-panel" *ngIf="submitting || progress">
            <header>
                <mat-icon *ngIf="!isErrored && !isDone" class="spin">sync</mat-icon>
                <mat-icon *ngIf="isDone" class="done">check_circle</mat-icon>
                <mat-icon *ngIf="isErrored" class="errored">error</mat-icon>
                <h3 *ngIf="!isErrored && !isDone">Creating <strong>{{ draft.basics.name }}</strong>…</h3>
                <h3 *ngIf="isDone">Done — opening your new organization…</h3>
                <h3 *ngIf="isErrored">Couldn't finish creating <strong>{{ draft.basics.name }}</strong></h3>
            </header>

            <mat-progress-bar
                [mode]="progressFraction === null ? 'indeterminate' : 'determinate'"
                [value]="(progressFraction ?? 0) * 100"
                [color]="isErrored ? 'warn' : 'primary'">
            </mat-progress-bar>

            <ul class="stage-list">
                <li *ngFor="let row of stages"
                    [class.done]="stageStatus(row) === 'done'"
                    [class.active]="stageStatus(row) === 'active'"
                    [class.pending]="stageStatus(row) === 'pending'">
                    <mat-icon [class.spin]="stageStatus(row) === 'active'">{{ stageIcon(row) }}</mat-icon>
                    <span class="stage-label">{{ row.label }}</span>
                    <span class="stage-message muted" *ngIf="stageStatus(row) === 'active' && progress?.message">
                        {{ progress?.message }}
                    </span>
                </li>
            </ul>

            <p class="muted footnote" *ngIf="!progress && submitting">
                Connecting to the create-organization service…
            </p>
        </section>

        <!-- Phase B.2b — per-row staff stage outcomes once the orchestrator
             finishes the staff stage. The report is written by the backend
             before the final `done` ping, so it surfaces both for in-flight
             submissions and once we've landed on success. -->
        <section class="staff-report" *ngIf="staffStageReport as report">
            <header>
                <mat-icon>group</mat-icon>
                <h3>Staff invitations</h3>
            </header>
            <p class="muted">
                {{ stagedSuccessCount }} of {{ report.totalRows }} staged successfully.
                <ng-container *ngIf="stagedFailedRows.length">
                    {{ stagedFailedRows.length }} failed or skipped.
                </ng-container>
            </p>
            <ul class="staff-report__rows" *ngIf="stagedFailedRows.length">
                <li *ngFor="let row of stagedFailedRows">
                    <strong>{{ row.email || '(no email)' }}</strong>
                    — {{ outcomeLabel(row.outcome) }}<ng-container *ngIf="row.reason">: {{ row.reason }}</ng-container>
                </li>
            </ul>
        </section>

        <div class="error-banner" *ngIf="errorMessage && !submitting">
            <mat-icon>error_outline</mat-icon>
            <span>{{ errorMessage }}</span>
        </div>

        <!-- Phase B.2b — autoSendInvitations toggle (spec §6.4). Default OFF
             in Phase B: rows are pre-staged into invitations + pending
             approvals, but no emails go out until Phase C wires the sender. -->
        <div class="auto-send-toggle" *ngIf="!isDone && draft.staff?.length">
            <mat-slide-toggle
                [checked]="draft.autoSendInvitations === true"
                [disabled]="submitting"
                (change)="setAutoSendInvitations($event.checked)"
            >
                Email invitations to new users immediately
            </mat-slide-toggle>
            <p class="muted footnote">
                Off by default in this release. Staff rows are pre-staged and visible to invitees once they sign in;
                emails will start sending in a later release.
            </p>
        </div>

        <div class="submit-bar" *ngIf="!isDone">
            <button
                mat-raised-button
                color="primary"
                class="submit-btn"
                (click)="onSubmit()"
                [disabled]="submitting || !draft.basics.name?.trim()"
            >
                <mat-spinner diameter="20" *ngIf="submitting"></mat-spinner>
                <ng-container *ngIf="!submitting">
                    <mat-icon>rocket_launch</mat-icon> Create organization
                </ng-container>
                <ng-container *ngIf="submitting">Creating…</ng-container>
            </button>
            <p class="footnote">
                You'll become the CEO and owner. A CEO hat with all features and permissions is created automatically.
            </p>
        </div>
    </section>
</ng-container>
